<script setup>
import {
  ref,
  reactive,
  defineProps,
  defineEmits,
  computed,
  watch,
  onMounted,
} from "vue";

const props = defineProps({
  devices: Array,
  loading: Boolean,
  hideRefresh: Boolean,
});

const emits = defineEmits(["view", "refresh"]);
const searchField = ref("");
const columnsMenu = ref(false);
const loading = ref(false);

var headers = reactive([
  {
    title: "Device Name",
    value: "Computer_Name",
    sortable: true,
    show: false,
    default: true,
  },
  {
    title: "Serial Number",
    value: "Serial_Number",
    sortable: true,
    show: false,
    default: true,
  },
  {
    title: "Username",
    value: "username",
    sortable: true,
    show: false,
    default: true,
  },
  {
    title: "Full Name",
    value: "fullName",
    sortable: true,
    show: false,
    default: true,
  },
  { title: "Model", value: "modelName", sortable: true, show: false },
  {
    title: "Last Contact",
    value: "patchLastContact",
    sortable: true,
    show: false,
    default: false,
  },
  {
    title: "Configuration Group",
    value: "configurationName",
    sortable: true,
    show: false,
    default: true,
  },
  {
    title: "Updates Available",
    value: "updatesAvailableCount",
    sortable: true,
    show: false,
    default: true,
  },
]);

watch(headers, (newHeaders) => {
  //  console.log("Headers updated:", newHeaders);
  if (!loading.value) {
    // Save headers to local storage
    localStorage.setItem("patch24DeviceHeaders", JSON.stringify(newHeaders));
  }
});

function resetCols() {
  headers.forEach((header) => {
    header.show = header.default;
  });
  localStorage.setItem("patch24DeviceHeaders", JSON.stringify(headers));
}

onMounted(() => {
  loading.value = true;
  // Load headers from local storage if available
  try {
    const storedHeaders = localStorage.getItem("patch24DeviceHeaders");
    if (storedHeaders) {
      const parsedHeaders = JSON.parse(storedHeaders);
      parsedHeaders.forEach((header) => {
        const existingHeader = headers.find((h) => h.value === header.value);
        if (existingHeader) {
          existingHeader.show = header.show;
        }
      });
    } else {
      resetCols();
    }
  } catch (error) {
    resetCols();
  }

  loading.value = false;
});

var computedHeaders = computed(() => {
  return headers.filter((header) => header.show);
});

// Convert date time to relative time either seconds, minutes, hours, or days if more than 3 days return date
const formatDate = (date) => {
  const now = new Date();
  const diff = Math.abs(now - new Date(date));
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return new Date(date).toLocaleString();
  } else if (days > 0) {
    return `${days} days ago`;
  } else if (hours > 0) {
    if (hours === 1) {
      return "1 hour ago";
    }
    return `${hours} hours ago`;
  } else if (minutes > 0) {
    if (minutes === 1) {
      return "1 minute ago";
    }
    return `${minutes} minutes ago`;
  } else {
    return `${seconds} seconds ago`;
  }
};

function rowClick(item, selectedRow) {
  emits("view", selectedRow.item);
}

const sortBy = ref([{ key: "Computer_Name", order: "ascending" }]);
</script>

<template>
  <v-row>
    <v-col cols="3">
      <v-sheet rounded="lg" class="ma-1" elevation="1">
        <v-text-field
          v-model="searchField"
          hide-details
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="solo"
          placeholder="Search anything..."
          clearable
        ></v-text-field>
      </v-sheet>
    </v-col>
    <v-spacer></v-spacer>
    <v-col cols="auto" class="mt-2 pr-0" v-if="props.hideRefresh !== true">
      <v-btn @click="emits('refresh')">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-col>
    <v-col cols="auto" class="ma-2 pl-0">
      <v-btn class="ml-2">
        <v-icon>mdi-format-columns</v-icon>
        <v-menu
          activator="parent"
          v-model="columnsMenu"
          :close-on-content-click="false"
        >
          <v-list>
            <v-list-item>
              <v-list-item-action>
                <v-btn
                  prepend-icon="mdi-close"
                  variant="text"
                  @click="columnsMenu = !columnsMenu"
                  >Close</v-btn
                >
                <v-btn
                  prepend-icon="mdi-refresh"
                  variant="text"
                  @click="resetCols()"
                  >Reset</v-btn
                >
              </v-list-item-action>
            </v-list-item>

            <v-list-item
              v-for="header in headers"
              :key="header.value"
              @click="header.show = !header.show"
            >
              <v-list-item-action>
                <v-checkbox-btn v-model="header.show" :label="header.title" />
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-btn>
    </v-col>
  </v-row>

  <v-sheet rounded="lg" elevation="1" class="ma-1">
    <v-data-table
      :headers="computedHeaders"
      :items="props.devices"
      :search="searchField"
      no-data-text="No Devices"
      loading-text="Loading Devices..."
      must-sort
      v-model:sort-by="sortBy"
      :loading="props.loading"
      @click:row="rowClick"
      hover
    >
      <template v-slot:[`item.patchLastContact`]="{ value }">
        {{ formatDate(value) }}
      </template>
      <template v-slot:[`item.Last_Check_in`]="{ value }">
        {{ formatDate(value) }}
      </template>
      <template v-slot:[`item.updatesAvailableCount`]="{ value }">
        {{ value || 0 }}
      </template>
    </v-data-table>
  </v-sheet>
</template>
